import React, { useState } from 'react';
import Icon from '../shared/Icon';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ExtractionTable from './ExtractionTable';
import './StructuredResponsePanel.css';

/**
 * Safely parse a JSON string, returning null on failure.
 */
const safeParse = (jsonString) => {
  if (typeof jsonString !== 'string') return jsonString;
  try {
    return JSON.parse(jsonString);
  } catch {
    return null;
  }
};

/**
 * Renders collapsible "Show reasoning" and "View supporting data" sections
 * for a structured agent response.
 *
 * Evidence items are shaped as:
 *   { source: string, data: string (JSON) }
 * where data must be JSON.parsed before rendering.
 *
 * @param {Object} props
 * @param {string} [props.reasoning] - Analytical reasoning text (markdown)
 * @param {Array<{source: string, data: string}>} [props.evidence] - Evidence items
 */
const StructuredResponsePanel = ({ reasoning, evidence }) => {
  const [reasoningExpanded, setReasoningExpanded] = useState(false);
  const [evidenceExpanded, setEvidenceExpanded] = useState(false);

  const hasReasoning = reasoning && reasoning.trim().length > 0;
  const hasEvidence = Array.isArray(evidence) && evidence.length > 0;

  if (!hasReasoning && !hasEvidence) return null;

  // Parse each evidence item's data and normalise to array of objects for tables
  const parsedEvidence = hasEvidence
    ? evidence
        .map((item) => {
          const parsed = safeParse(item.data);
          if (parsed == null) return null;
          const rows = Array.isArray(parsed) ? parsed : [parsed];
          return { source: item.source || 'Unknown source', rows };
        })
        .filter(Boolean)
    : [];

  return (
    <div className="structured-response-panel">
      {hasReasoning && (
        <div className="sr-section">
          <div
            className="sr-section-header"
            onClick={() => setReasoningExpanded(prev => !prev)}
          >
            <span className="sr-section-icon">
              {reasoningExpanded ? '▼' : '▶'}
            </span>
            <Icon name="psychology" className="sr-section-material-icon" size={18} />
            <span className="sr-section-title">Show reasoning</span>
          </div>
          {reasoningExpanded && (
            <div className="sr-section-content sr-reasoning-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {typeof reasoning === 'string' ? reasoning.replace(/\[([^\]]*)\]\(sandbox:[^)]*\)/gi, '') : reasoning}
              </ReactMarkdown>
            </div>
          )}
        </div>
      )}

      {parsedEvidence.length > 0 && (
        <div className="sr-section">
          <div
            className="sr-section-header"
            onClick={() => setEvidenceExpanded(prev => !prev)}
          >
            <span className="sr-section-icon">
              {evidenceExpanded ? '▼' : '▶'}
            </span>
            <Icon name="table_chart" className="sr-section-material-icon" size={18} />
            <span className="sr-section-title">
              View supporting data ({parsedEvidence.length} source{parsedEvidence.length !== 1 ? 's' : ''})
            </span>
          </div>
          {evidenceExpanded && (
            <div className="sr-section-content sr-evidence-content">
              {parsedEvidence.map((group, i) => (
                <div key={i} className="sr-evidence-group">
                  <div className="sr-evidence-source">{group.source}</div>
                  <ExtractionTable data={group.rows} title={group.source} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StructuredResponsePanel;
